/**
 * CR-7231 rework: state for the Contract Expiry Alert card — which scope it is
 * configuring (Group Default, one site, or the platform for Helix), the value
 * that applies there, and save / clear.
 *
 * Saves and clears always target the scope the server last answered for
 * (`setting.scope` / `setting.id`), never the picker's pending choice, so a
 * slow response can never send a value to a scope that is not on screen.
 */
import { useState } from 'react';

import {
  platformSettingsAPI,
  apiErrorMessage,
  type ContractExpiryAlertSetting,
} from '../../../lib/api';
import type { ScopeSelection } from '../../../lib/api/performanceSettings';
import { useViewKey } from '../../../contexts/ViewContext';
import { useFetchData } from '../../../hooks/useFetchData';
import { toast } from 'sonner';

export interface UseContractExpiryAlertResult {
  setting: ContractExpiryAlertSetting | null;
  loading: boolean;
  error: string | null;
  saving: boolean;
  clearing: boolean;
  /** Switch the card to another scope from the picker. */
  selectScope: (target: ScopeSelection) => void;
  /** Give the scope on screen its own value; resolves false when refused. */
  save: (days: number) => Promise<boolean>;
  /** Drop the scope's own value so it inherits again. */
  clear: () => Promise<void>;
}

/** The scope a response is about, as the API takes it. */
export function selectionOf(setting: ContractExpiryAlertSetting): ScopeSelection {
  return setting.id === null
    ? { scope: setting.scope }
    : { scope: setting.scope, id: setting.id };
}

/** How the toast names a scope: "Acme (Group Default)", a site, or the platform. */
export function scopeLabel(setting: ContractExpiryAlertSetting): string {
  if (setting.scope === 'group') return `${setting.name} (Group Default)`;
  return setting.name;
}

const plural = (days: number) => `${days} day${days === 1 ? '' : 's'} prior`;

/**
 * Load and edit the Contract Expiry Alert for the scope the picker names.
 *
 * With no pick yet, the server chooses: the group in a group view (group
 * admins), else the Admin Setup site in view; Helix gets the platform default.
 * A change of view (the top-bar switcher) drops the pick and starts again.
 *
 * @returns the setting on screen, load / save state, and the actions
 * @example
 *   const { setting, selectScope, save, clear } = useContractExpiryAlert();
 */
export function useContractExpiryAlert(): UseContractExpiryAlertResult {
  const viewKey = useViewKey();
  // The pick remembers the view it was made in; a different view ignores it.
  const [pick, setPick] = useState<{ viewKey: string; target?: ScopeSelection }>({ viewKey });
  const target = pick.viewKey === viewKey ? pick.target : undefined;
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);

  const { data: setting, loading, error, setData } = useFetchData<ContractExpiryAlertSetting>(
    async () => (await platformSettingsAPI.getContractExpiryAlert(target)).data,
    { deps: [viewKey, target?.scope, target?.id] },
  );

  const selectScope = (next: ScopeSelection) => setPick({ viewKey, target: next });

  const save = async (days: number): Promise<boolean> => {
    if (!setting) return false;
    setSaving(true);
    try {
      const { data } = await platformSettingsAPI.setContractExpiryAlert(days, selectionOf(setting));
      setData(data);
      toast.success(`Contract Expiry Alert for ${scopeLabel(data)} set to ${plural(data.days)}`);
      return true;
    } catch (err: unknown) {
      console.error('Error saving Contract Expiry Alert:', err);
      toast.error(apiErrorMessage(err, 'Failed to save Contract Expiry Alert'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const clear = async (): Promise<void> => {
    if (!setting) return;
    setClearing(true);
    try {
      const { data } = await platformSettingsAPI.clearContractExpiryAlert(selectionOf(setting));
      setData(data);
      const from = data.source === 'group' ? 'the Group Default' : 'the platform default';
      toast.success(`${scopeLabel(data)} now follows ${from} (${plural(data.days)})`);
    } catch (err: unknown) {
      console.error('Error clearing Contract Expiry Alert:', err);
      toast.error(apiErrorMessage(err, 'Failed to clear Contract Expiry Alert'));
    } finally {
      setClearing(false);
    }
  };

  return { setting, loading, error, saving, clearing, selectScope, save, clear };
}
